import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useToast } from "@modules/ui/components/huToast"
import { Dialog } from "primereact/dialog"
import React, { useEffect, useImperativeHandle, useState } from "react"

export type FullscreenModalRef = {
  open: () => void
}
type FullscreenModalProps = {
  children: React.ReactNode
  ref: React.Ref<FullscreenModalRef>
}

export function FullscreenModal({ children, ref }: FullscreenModalProps) {
  const { browserProfileService } = useServicesContext()
  const toast = useToast()

  const [showModal, setShowModal] = useState(false)
  const [showIcons, setShowIcons] = useState(false)
  const [isThemeSwap, setIsThemeSwap] = useState(browserProfileService.profileTheme.get().theme === "owner")

  useImperativeHandle(ref, () => ({
    open: () => {
      if (document.documentElement.requestFullscreen) {
        document.documentElement
          .requestFullscreen()
          .catch((err) => {
            toast.show({
              severity: "error",
              summary: word("program.session.displayFullScreenNotSupported.title"),
              detail: err.message,
            })
          })
          .then(() => {
            window.addEventListener("mousemove", handleMouseMove)
            // swap to ligh theme if needed
            if (browserProfileService.profileTheme.get().theme === "owner") {
              browserProfileService.setTheme("athlete")
              setIsThemeSwap(true)
            }
            setShowModal(true)
          })
      } else {
        toast.show({
          severity: "error",
          summary: word("program.session.displayFullScreenNotSupported.title"),
          detail: word("program.session.displayFullScreenNotSupported.content"),
        })
      }
    },
  }))

  let iconTimeout: ReturnType<typeof setTimeout>

  const handleMouseMove = () => {
    setShowIcons(true)
    // hide icons after 2 sec of inactivity
    clearTimeout(iconTimeout)
    iconTimeout = setTimeout(() => {
      setShowIcons(false)
    }, 2000)
  }

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleMouseMove)

      //restore owner theme on exit (can occur out of onHide function if navigation changes)
      if (isThemeSwap) browserProfileService.setTheme("owner")
    }
  }, [])

  return (
    <Dialog
      visible={showModal}
      onHide={() => {
        //restore owner theme on exit
        if (isThemeSwap) browserProfileService.setTheme("owner")

        setShowModal(false)

        document.exitFullscreen().catch((err) => {
          console.error(`Error exiting browser fullscreen: ${err.message}`)
        })
      }}
      maximized
      headerClassName={"transition-duration-500 transition-all " + (showIcons ? "opacity-100" : "opacity-0")}
      contentClassName="m-0 p-0"
    >
      {children}
    </Dialog>
  )
}
